import { Injectable, RendererFactory2, Inject, Renderer2 } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { DOCUMENT } from '@angular/common';

export interface ScriptModel {
  name: string; // the added id tag. will use this info to check if it got already loaded
  src: string; // the url to the source.
  loaded: boolean; // check manually if your script got already loaded
  async?: boolean; // will add the async tag to scripts
  preconnect?: string; // if set, will add a link rel=preconnect with given link
  prefetch?: string; // if set, will add a link rel=dns-prefetch to header
  skipLoadCheck?: boolean;
}

/**
 * lets you add third party scripts to the header.
 * On SSR: adds the script tags to your head. Won't trigger on load or the subsrciption!
 * On Client: adds the script tags to your head. Will trigger onLoad() and subscriptions.
 *  */
@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private scripts: ScriptModel[] = [];
  private renderer2: Renderer2;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer2 = rendererFactory.createRenderer(null, null);
  }

  public load(script: ScriptModel): Observable<ScriptModel> {
    return new Observable<ScriptModel>((observer: Observer<ScriptModel>) => {
      const existingScript = this.scripts.find(s => s.name === script.name);

      // Complete if already loaded
      if (!script.skipLoadCheck && existingScript && existingScript.loaded) {
        observer.next(existingScript);
        observer.complete();
      } else {
        // Add the script
        this.scripts = [...this.scripts, script];

        // Load the script
        const scriptElement = this.renderer2.createElement('script');
        scriptElement.type = 'text/javascript';
        scriptElement.src = script.src;
        scriptElement.id = script.name;
        if (script.async) {
          scriptElement.async = true;
        }

        scriptElement.onload = () => {
          script.loaded = true;
          observer.next(script);
          observer.complete();
        };

        scriptElement.onerror = () => {
          console.error("Couldn't load script " + script.src);
          observer.error("Couldn't load script " + script.src);
        };

        this.renderer2.appendChild(this.document.head, scriptElement);

        if (script.preconnect) {
          const link = this.renderer2.createElement('link');
          link.href = script.preconnect;
          link.rel = 'preconnect';
          this.renderer2.appendChild(this.document.head, link);
        } else if (script.prefetch) {
          const link = this.renderer2.createElement('link');
          link.href = script.prefetch;
          link.rel = 'dns-prefetch';
          this.renderer2.appendChild(this.document.head, link);
        }
      }
    });
  }

  public loadCSS(css: ScriptModel): Observable<ScriptModel> {
    return new Observable<ScriptModel>((observer: Observer<ScriptModel>) => {
      const existingScript = this.scripts.find(s => s.name === css.name);

      // Complete if already loaded
      if ((existingScript && existingScript.loaded) || this.document.getElementById(css.name)) {
        observer.next(existingScript);
        observer.complete();
      } else {
        // Add the script
        this.scripts = [...this.scripts, css];

        // Load the script
        const scriptElement = this.renderer2.createElement('link');
        scriptElement.type = 'text/css';
        scriptElement.rel = 'stylesheet';
        scriptElement.href = css.src;
        scriptElement.id = css.name;

        scriptElement.onload = () => {
          observer.next(css);
          css.loaded = true;
          observer.complete();
        };

        scriptElement.onerror = () => {
          observer.error("Couldn't load css " + css.src);
        };
        this.renderer2.appendChild(this.document.head, scriptElement);
      }
    });
  }
}
